<template>
    <div class="flex flex-col h-full">
        <div class="flex flex-row items-center mt-1">
            <h1 class="flex-grow text-3xl font-semibold text-gray-900 -mt-1">
                {{ $t('orders.listing.pageTitle') }}
            </h1>
        </div>

        <div class="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div class="bg-white overflow-hidden shadow rounded-lg">
                <div class="px-4 py-5 sm:p-6">
                    <dl>
                        <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                            {{ $t('orders.listing.stats.available') }}
                        </dt>
                        <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
                            {{ unactivatedCount }}
                        </dd>
                    </dl>
                </div>
            </div>
            <div class="bg-white overflow-hidden shadow rounded-lg">
                <div class="px-4 py-5 sm:p-6">
                    <dl>
                        <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                            {{ $t('orders.listing.stats.activated') }}

                            <!--                            <span class="text-xs text-gray-300">-->
                            <!--                                ({{ $t('orders.listing.yearToDate') }})-->
                            <!--                            </span>-->
                        </dt>
                        <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
                            {{ activatedCount }}
                        </dd>
                    </dl>
                </div>
            </div>
            <div class="bg-white overflow-hidden shadow rounded-lg">
                <div class="px-4 py-5 sm:p-6">
                    <dl>
                        <dt class="text-sm leading-5 font-medium text-gray-500 truncate">
                            {{ $t('orders.listing.stats.purchased') }}

                            <!--                            <span class="text-xs text-gray-300">-->
                            <!--                                ({{ $t('orders.listing.yearToDate') }})-->
                            <!--                            </span>-->
                        </dt>
                        <dd class="mt-1 text-3xl leading-9 font-semibold text-gray-900">
                            {{ purchasedCount }}
                        </dd>
                    </dl>
                </div>
            </div>
        </div>

        <div class="flex flex-col min-h-0 h-full rounded-md shadow-lg bg-white overflow-hidden mt-6">
            <section class="flex-1 relative overflow-hidden">
                <div class="flex flex-col w-full h-full overflow-y-auto">
                    <apexo-table
                        :clickable="false"
                        :columns="columns"
                        :loading="$wait.is('fetching.orders')"
                        :rows="formatedOrders"
                        ref="body"
                    >
                        <template #no-results>
                            <p class="text-xl">
                                {{ $t('orders.listing.noOrders') }}
                            </p>
                        </template>
                    </apexo-table>
                </div>
            </section>

            <section class="flex justify-between items-center border-t border-gray-200 bg-white py-3 px-8 text-sm" v-if="pagination.total">
                <pagination-info
                    :current="pagination.currentPage"
                    :paginated-item-name="$t('orders.pageTitle')"
                    :per-page="perPage"
                    :total="pagination.total"
                />

                <pagination
                    :current-page="pagination.currentPage"
                    :last-page="pagination.lastPage"
                    @update="setCurrentPage"
                    v-if="pagination.total > perPage"
                />
            </section>
        </div>

        <order-modal />
    </div>
</template>

<script>
    import { mapActions, mapState } from 'vuex';
    import Pagination from '@/components/Pagination.vue';
    import PaginationInfo from '@/components/PaginationInfo.vue';
    import OrderModal from '@/components/Orders/OrderModal.vue';
    import ApexoTable from '@/components/Elements/ApexoTable.vue';

    export default {
        components: {
            ApexoTable,
            OrderModal,
            Pagination,
            PaginationInfo,
        },

        data() {
            return {
                activatedCount: 0,
                pagination: {
                    from: 0,
                    to: 0,
                    total: 0,
                    currentPage: 1,
                    lastPage: 0,
                },
                perPage: 25,
                purchasedCount: 0,
            };
        },

        computed: {
            ...mapState([
                'licenseTypes',
                'orders',
            ]),

            columns() {
                return [
                    {
                        name: 'number',
                        label: this.$t('orders.listing.number'),
                        icon: 'regular/single-neutral',
                        className: 'flex-1',
                    },
                    {
                        name: 'content',
                        label: this.$t('orders.listing.content'),
                        icon: 'regular/phone',
                        className: 'flex-4',
                    },
                    {
                        name: 'quantity',
                        label: this.$t('orders.listing.quantity'),
                        icon: 'regular/phone',
                        className: 'flex-1 justify-center',
                    },
                    {
                        name: 'date',
                        label: this.$t('orders.listing.date'),
                        icon: 'regular/earth-1',
                        className: 'flex-2',
                    },
                ];
            },

            formatedOrders() {
                return this.orders.map(order => {
                    return {
                        id: order.id,
                        number: order.number || '-',
                        quantity: order.items[0].quantity,
                        content: this.getOrderItemsString(order),
                        date: this.formatDateTime(order.created_at),
                    };
                });
            },

            unactivatedCount() {
                return this.licenseTypes.reduce((a, b) => a + (b.unactivatedLicenses || 0), 0);
            },
        },

        methods: {
            ...mapActions([
                'getOrders',
            ]),

            async fetchOrders() {
                this.$wait.start('fetching.orders');

                const payload = {
                    page: this.pagination.currentPage,
                    perPage: this.perPage,
                };

                try {
                    const { meta } = await this.getOrders(payload);

                    this.pagination.from = meta.from;
                    this.pagination.to = meta.to;
                    this.pagination.total = meta.total;
                    this.pagination.lastPage = meta.last_page;
                } catch (error) {
                    // this.$notify.show({
                    //     type: 'error',
                    //     message: this.$t('campaigns.listing.errors.fetching'),
                    // });
                    console.log('Error fetching orders');

                    throw error;
                } finally {
                    this.$wait.end('fetching.orders');
                }
            },

            async fetchPurchasedCount() {
                this.$wait.start('fetching.purchasedCount');

                try {
                    const response = await this.$axios.get('licenses/purchased-count');

                    this.purchasedCount = response.data;
                } catch (error) {
                    console.log('Error fetching purchased licenses count.');

                    throw error;
                } finally {
                    this.$wait.end('fetching.purchasedCount');
                }
            },

            async fetchActivatedCount() {
                this.$wait.start('fetching.activatedCount');

                try {
                    const response = await this.$axios.get('licenses/activated-count');

                    this.activatedCount = response.data;
                } catch (error) {
                    console.log('Error fetching purchased licenses count.');

                    throw error;
                } finally {
                    this.$wait.end('fetching.activatedCount');
                }
            },

            getOrderItemsString(order) {
                const licenseType = order.items[0].licenseType.localized_name[this.$i18n.locale];
                const platform = this.$t(`global.platform.${order.items[0].licenseType.platform}`);

                return `${licenseType} (${platform})`;
            },

            setCurrentPage(page) {
                this.pagination.currentPage = page;

                this.fetchOrders();
            },
        },

        created() {
            this.fetchOrders();
            this.fetchPurchasedCount();
            this.fetchActivatedCount();
        },
    };
</script>
